import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, TouchEvent } from 'react';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CallToAction } from '../components/CallToAction';

const googleTestimonialsByLang: Record<string, { name: string; excerpt: string }[]> = {
  de: [
    {
      name: 'Michele Stelluti',
      excerpt:
        'Herr Staisch, ist unglaublich Nett, hilfsbereit und der einzige Handwerker. Der uns nicht übers Ohr gehauenen hat.. Zum Glück gibt es noch einige wenige Gentleman unter den Handwerkern. Wie der Herr Staisch es ist! Danke'
    },
    {
      name: 'Gaetano Giambrone',
      excerpt:
        'Herr Staisch war an einem Feiertag bei uns vor Ort und hat alles sehr gründlich geprüft. Er ist supernett, freundlich und hilfsbereit und hat uns alles verständlich erklärt. Man merkt sofort, dass er kompetent ist und genau weiß, was er tut. Wir sind sehr dankbar für seine professionelle Arbeit, fühlten uns sehr gut beraten und können ihn wirklich nur weiterempfehlen.'
    },
    {
      name: 'Grgic Tihomir',
      excerpt:
        'Herr Staisch ist unglaublich nett und hilfsbereit. Sofort auf den Notfall aufmerksam geworden und reagiert und einen guten Rat gegeben. Auf jeden Fall empfehlenswert.'
    },
    {
      name: 'Ilenia Toscano',
      excerpt:
        'Wir hatten ein Telefonisches Gespräch mit ihm. Total hilfsbereit und lieb, hat uns super weitergeholfen.'
    },
    {
      name: 'Stefan (Local Guide)',
      excerpt:
        'Wir haben ein älteres Haus gekauft und da gab es einiges zu erneuern, viel mehr als wir zuerst geplant hatten. Herr Staisch und seine Mitarbeiter haben dabei professionelle Arbeit geleistet, wenn die Zeit knapp war auch sehr kurzfristig, und uns auch mit einigen Ratschlägen zur Seite gestanden. So konnte ich einige Arbeiten selbst erledigen (Leitungen ziehen, Kabelkanäle verlegen, Steckdosen anschließen) und Herr Staisch übernahm die Abschlussprüfung. Damit hat es mit unserer Planung doch noch hingehauen.'
    },
    {
      name: 'Lili Wild (LeeWee)',
      excerpt:
        'Wir haben im Raum Stuttgart ein Häuschen gekauft und da waren einige Elektroarbeiten fällig. Herr Staisch und seine Truppe verfügen über ein solides Fachwissen, geben gute Ratschläge, die passend für den Kunden sind. Sie sind zuverlässig, freundlich, gehen auf die Wünsche ein, halten Termine ein. Am meisten schätzen wir, dass Herr Staisch gewerksübergreifend, zukunftssicher und innovativ denkt. Wir sind überaus zufrieden - er ist der Elektriker unseres Vertrauens, welchen wir bereits mehrmals beauftragt haben und jedes Mal sind wir rundum sehr zufriedene Kunden. Wir empfehlen sie jederzeit wieder weiter! :-)'
    },
    {
      name: 'Melanie Zipperle',
      excerpt:
        'Super nettes und kompetentes Team, die Arbeit wird schnell und zur vollsten Zufriedenheit ausgeführt. Und auch kurzfristige Wünsche wurden schnell & unkompliziert umgesetzt. Vielen Dank Herr Staisch für alles, ich empfehle Sie gerne weiter ... Empfehlenswert!!!'
    },
    {
      name: 'Simone Zahn',
      excerpt:
        'Sehr kompetent, pünktlich und tolle Beratung. Immer wieder, echte Seltenheit, so einen Handwerksbetrieb zu bekommen'
    },
    {
      name: 'Stv.',
      excerpt:
        'Herr Staisch ist sehr freundlich und kompetent. Er arbeitet schnell und gründlich.'
    },
    {
      name: 'Ardan (Local Guide)',
      excerpt:
        'Sehr zuverlässig und einer der letzten guten Handwerker ein wirklicher Mann von Fach! Gerne wieder!'
    },
    {
      name: 'Ali M (Local Guide)',
      excerpt:
        'Super freundlicher Mitarbeiter, professionell und macht seine Arbeit ganz genau. Sehr gerne kontaktieren wir Sie wieder wenn was sein sollte. Vielen Dank !'
    },
    {
      name: 'Heike Krämer (Local Guide)',
      excerpt: 'Super freundlich, zuverlässig, kompetentes Fachwissen, pünktlich'
    }
  ],
  en: [
    {
      name: 'Michele Stelluti',
      excerpt:
        'Mr. Staisch is incredibly kind and helpful and the only tradesman who did not try to rip us off. Luckily there are still a few gentlemen among craftsmen—Mr. Staisch is one of them! Thank you.'
    },
    {
      name: 'Gaetano Giambrone',
      excerpt:
        'Mr. Staisch came to us on a public holiday and checked everything very thoroughly. He is super kind, friendly and helpful and explained everything clearly. You can tell he is competent and knows exactly what he is doing. We are very grateful for his professional work, felt well advised and can highly recommend him.'
    },
    {
      name: 'Grgic Tihomir',
      excerpt:
        'Mr. Staisch is incredibly kind and helpful. He noticed the emergency immediately, reacted and gave good advice. Definitely recommended.'
    },
    {
      name: 'Ilenia Toscano',
      excerpt: 'We had a phone call with him. Totally helpful and kind, he helped us a lot.'
    },
    {
      name: 'Stefan (Local Guide)',
      excerpt:
        'We bought an older house and there was a lot to renovate, much more than we had planned. Mr. Staisch and his team did professional work, even on short notice when time was tight, and gave us good advice. I was able to do some tasks myself (pulling cables, laying cable ducts, wiring sockets) and Mr. Staisch handled the final inspection. Our plan still worked out.'
    },
    {
      name: 'Lili Wild (LeeWee)',
      excerpt:
        'We bought a small house in the Stuttgart area and there was some electrical work to do. Mr. Staisch and his team have solid expertise and give advice tailored to the customer. They are reliable, friendly, respond to wishes and keep appointments. We especially appreciate that Mr. Staisch thinks across trades, future‑proof and innovative. We are very satisfied—he is our trusted electrician, whom we have hired several times, and we have always been completely satisfied. We recommend them anytime! :-)'
    },
    {
      name: 'Melanie Zipperle',
      excerpt:
        'Super friendly and competent team. The work is done quickly and to our full satisfaction. Even short‑notice requests were implemented quickly and easily. Thank you, Mr. Staisch, for everything—highly recommended!'
    },
    {
      name: 'Simone Zahn',
      excerpt:
        "Very competent, punctual and great advice. Anytime again—it's rare to find a crafts business like this."
    },
    {
      name: 'Stv.',
      excerpt: 'Mr. Staisch is very friendly and competent. He works quickly and thoroughly.'
    },
    {
      name: 'Ardan (Local Guide)',
      excerpt:
        'Very reliable and one of the last good tradesmen—a true professional! Happy to use again.'
    },
    {
      name: 'Ali M (Local Guide)',
      excerpt:
        'Super friendly employee, professional and very precise in his work. We will gladly contact you again if needed. Thank you!'
    },
    {
      name: 'Heike Krämer (Local Guide)',
      excerpt: 'Super friendly, reliable, competent expertise, punctual.'
    }
  ],
  tr: [
    {
      name: 'Michele Stelluti',
      excerpt:
        'Bay Staisch inanılmaz nazik ve yardımsever, bizi kandırmayan tek usta oldu. Neyse ki ustalar arasında hâlâ birkaç beyefendi var—Bay Staisch de onlardan! Teşekkürler.'
    },
    {
      name: 'Gaetano Giambrone',
      excerpt:
        'Bay Staisch bir tatil gününde geldi ve her şeyi çok titiz kontrol etti. Çok nazik, güler yüzlü ve yardımseverdi; her şeyi anlaşılır şekilde açıkladı. Ne yaptığını çok iyi bildiği hemen anlaşılıyor. Profesyonel çalışması için çok minnettarız, çok iyi bilgilendirildik ve gönül rahatlığıyla tavsiye ederiz.'
    },
    {
      name: 'Grgic Tihomir',
      excerpt:
        'Bay Staisch inanılmaz nazik ve yardımsever. Acil durumu hemen fark edip müdahale etti ve iyi bir tavsiye verdi. Kesinlikle tavsiye edilir.'
    },
    {
      name: 'Ilenia Toscano',
      excerpt:
        'Kendisiyle telefonla görüştük. Çok yardımsever ve nazikti, bize çok yardımcı oldu.'
    },
    {
      name: 'Stefan (Local Guide)',
      excerpt:
        'Eski bir ev aldık ve yenilenmesi gereken çok şey vardı, ilk planladığımızdan çok daha fazla. Bay Staisch ve ekibi profesyonel çalıştı; zaman kısıtlı olduğunda bile çok kısa sürede destek oldular ve iyi tavsiyeler verdiler. Bazı işleri kendim yapabildim (kablo çekmek, kablo kanalı döşemek, priz bağlamak) ve Bay Staisch son kontrolü yaptı. Böylece planımız yine de tutmuş oldu.'
    },
    {
      name: 'Lili Wild (LeeWee)',
      excerpt:
        'Stuttgart bölgesinde küçük bir ev aldık ve bazı elektrik işleri gerekiyordu. Bay Staisch ve ekibi sağlam bir uzmanlığa sahip, müşteriye uygun tavsiyeler veriyor. Güvenilirler, nazikler, istekleri dikkate alıyor ve randevulara uyuyorlar. En çok, Bay Staisch’in farklı iş kollarını da düşünmesi, geleceğe dönük ve yenilikçi olması hoşumuza gidiyor. Çok memnunuz—güvendiğimiz elektrikçimiz oldu, kendisini birkaç kez görevlendirdik ve her seferinde çok memnun kaldık. Her zaman tavsiye ederiz! :-)'
    },
    {
      name: 'Melanie Zipperle',
      excerpt:
        'Çok nazik ve yetkin bir ekip; işler hızlı ve tam memnuniyetle tamamlandı. Kısa sürede yapılan talepler bile hızlı ve sorunsuz çözüldü. Her şey için teşekkürler Bay Staisch—şiddetle tavsiye!'
    },
    {
      name: 'Simone Zahn',
      excerpt:
        'Çok yetkin, dakik ve harika danışmanlık. Her zaman tekrar; böyle bir usta işletmeyi bulmak gerçekten nadir.'
    },
    {
      name: 'Stv.',
      excerpt: 'Bay Staisch çok nazik ve yetkin. Hızlı ve titiz çalışıyor.'
    },
    {
      name: 'Ardan (Local Guide)',
      excerpt:
        'Çok güvenilir ve kalan son iyi ustalardan—gerçek bir profesyonel! Memnuniyetle tekrar.'
    },
    {
      name: 'Ali M (Local Guide)',
      excerpt:
        'Çok güler yüzlü çalışan, profesyonel ve işini çok dikkatli yapıyor. Bir şey olursa sizi tekrar aramaktan memnun oluruz. Teşekkürler!'
    },
    {
      name: 'Heike Krämer (Local Guide)',
      excerpt: 'Çok güler yüzlü, güvenilir, uzman bilgisi güçlü, dakik.'
    }
  ],
  ru: [
    {
      name: 'Michele Stelluti',
      excerpt:
        'Г-н Штайш невероятно добрый и отзывчивый, единственный мастер, который нас не обманул. К счастью, среди мастеров ещё есть джентльмены — и г-н Штайш один из них! Спасибо.'
    },
    {
      name: 'Gaetano Giambrone',
      excerpt:
        'Г-н Штайш приехал к нам в праздничный день и очень тщательно всё проверил. Он очень доброжелательный, дружелюбный и отзывчивый, всё понятно объяснил. Сразу видно, что он компетентен и точно знает, что делает. Мы очень благодарны за его профессиональную работу, получили отличную консультацию и с удовольствием рекомендуем.'
    },
    {
      name: 'Grgic Tihomir',
      excerpt:
        'Г-н Штайш очень добрый и отзывчивый. Сразу заметил проблему, отреагировал и дал хороший совет. Однозначно рекомендую.'
    },
    {
      name: 'Ilenia Toscano',
      excerpt:
        'Мы созвонились с ним. Очень помог и был очень добрым, действительно выручил.'
    },
    {
      name: 'Stefan (Local Guide)',
      excerpt:
        'Мы купили старый дом, и там нужно было многое обновить — гораздо больше, чем мы планировали. Г-н Штайш и его сотрудники работали профессионально, при необходимости помогали очень быстро и давали советы. Я смог часть работ сделать сам (протянуть кабели, проложить кабель-каналы, подключить розетки), а г-н Штайш провёл финальную проверку. В итоге наш план всё же сработал.'
    },
    {
      name: 'Lili Wild (LeeWee)',
      excerpt:
        'Мы купили дом в районе Штутгарта, и там требовались электромонтажные работы. Г-н Штайш и его команда обладают крепкими знаниями, дают советы, подходящие клиенту. Они надежные, дружелюбные, учитывают пожелания и соблюдают сроки. Больше всего ценим, что г-н Штайш мыслит междисциплинарно, современно и с прицелом на будущее. Мы очень довольны — это наш электрик, которому доверяем; мы обращались к нему несколько раз и всегда оставались полностью довольны. Рекомендуем в любое время! :-)'
    },
    {
      name: 'Melanie Zipperle',
      excerpt:
        'Очень дружелюбная и компетентная команда, работа выполнена быстро и на высшем уровне. Даже срочные просьбы были решены быстро и без проблем. Большое спасибо, г-н Штайш — однозначно рекомендую!'
    },
    {
      name: 'Simone Zahn',
      excerpt:
        'Очень компетентно, пунктуально и отличная консультация. С удовольствием снова — редкость найти такую мастерскую.'
    },
    {
      name: 'Stv.',
      excerpt: 'Г-н Штайш очень дружелюбный и компетентный. Работает быстро и тщательно.'
    },
    {
      name: 'Ardan (Local Guide)',
      excerpt:
        'Очень надежный, один из последних хороших мастеров — настоящий профессионал! С удовольствием снова.'
    },
    {
      name: 'Ali M (Local Guide)',
      excerpt:
        'Очень дружелюбный сотрудник, профессионал и работает очень тщательно. Мы с радостью обратимся снова при необходимости. Спасибо!'
    },
    {
      name: 'Heike Krämer (Local Guide)',
      excerpt: 'Очень дружелюбная, надежная, компетентная, пунктуальная.'
    }
  ],
  hr: [
    {
      name: 'Michele Stelluti',
      excerpt:
        'Gospodin Staisch je nevjerojatno ljubazan i susretljiv, jedini majstor koji nas nije pokušao prevariti. Srećom, među obrtnicima još ima pravih džentlmena — a gospodin Staisch je jedan od njih! Hvala.'
    },
    {
      name: 'Gaetano Giambrone',
      excerpt:
        'Gospodin Staisch došao je k nama na blagdan i sve vrlo temeljito provjerio. Izuzetno je ljubazan, prijateljski i uslužan te nam je sve razumljivo objasnio. Odmah se vidi da je stručan i zna što radi. Vrlo smo zahvalni na profesionalnom radu, osjećali smo se odlično savjetovanima i možemo ga toplo preporučiti.'
    },
    {
      name: 'Grgic Tihomir',
      excerpt:
        'Gospodin Staisch je nevjerojatno ljubazan i uslužan. Odmah je primijetio hitnu situaciju, reagirao i dao dobar savjet. Svakako preporučujem.'
    },
    {
      name: 'Ilenia Toscano',
      excerpt:
        'Imali smo telefonski razgovor. Bio je vrlo susretljiv i ljubazan, puno nam je pomogao.'
    },
    {
      name: 'Stefan (Local Guide)',
      excerpt:
        'Kupili smo stariju kuću i bilo je puno toga za obnoviti, mnogo više nego što smo planirali. Gospodin Staisch i njegov tim odradili su profesionalan posao, čak i kada je bilo tijesno s vremenom, te su nam dali korisne savjete. Neke sam radove mogao odraditi sam (provlačenje kabela, postavljanje kabelskih kanala, spajanje utičnica), a g. Staisch je obavio završnu provjeru. Na kraju je naš plan ipak uspio.'
    },
    {
      name: 'Lili Wild (LeeWee)',
      excerpt:
        'Kupili smo kućicu u području Stuttgarta i bilo je potrebno dosta elektro radova. G. Staisch i njegov tim imaju solidno stručno znanje, daju savjete prilagođene klijentu. Pouzdani su, ljubazni, uvažavaju želje i poštuju termine. Najviše cijenimo što g. Staisch razmišlja interdisciplinarno, dugoročno i inovativno. Jako smo zadovoljni — on je naš električar od povjerenja; angažirali smo ga više puta i svaki put bili u potpunosti zadovoljni. Preporučujemo ih uvijek! :-)'
    },
    {
      name: 'Melanie Zipperle',
      excerpt:
        'Super ljubazan i kompetentan tim; posao je obavljen brzo i na potpuno zadovoljstvo. I hitne želje su riješene brzo i jednostavno. Hvala g. Staisch na svemu — preporučujemo!'
    },
    {
      name: 'Simone Zahn',
      excerpt:
        'Vrlo kompetentni, točni i odlični savjeti. Uvijek ponovno — prava rijetkost naći ovakav obrt.'
    },
    {
      name: 'Stv.',
      excerpt: 'Gospodin Staisch je vrlo ljubazan i stručan. Radi brzo i temeljito.'
    },
    {
      name: 'Ardan (Local Guide)',
      excerpt:
        'Vrlo pouzdan i jedan od posljednjih dobrih majstora — pravi stručnjak! Rado ponovno.'
    },
    {
      name: 'Ali M (Local Guide)',
      excerpt:
        'Super ljubazan zaposlenik, profesionalan i radi vrlo precizno. Rado ćemo vas ponovno kontaktirati ako bude potrebno. Hvala!'
    },
    {
      name: 'Heike Krämer (Local Guide)',
      excerpt: 'Vrlo ljubazni, pouzdani, stručno znanje, točni.'
    }
  ],
  it: [
    {
      name: 'Michele Stelluti',
      excerpt:
        'Il signor Staisch è incredibilmente gentile e disponibile ed è l’unico artigiano che non ha cercato di imbrogliarci. Per fortuna ci sono ancora veri gentiluomini tra gli artigiani — e il signor Staisch è uno di loro! Grazie.'
    },
    {
      name: 'Gaetano Giambrone',
      excerpt:
        'Il signor Staisch è venuto da noi in un giorno festivo e ha controllato tutto molto accuratamente. È super gentile, cordiale e disponibile e ci ha spiegato tutto in modo chiaro. Si vede subito che è competente e sa esattamente cosa fa. Siamo molto grati per il suo lavoro professionale, ci siamo sentiti ben consigliati e lo consigliamo vivamente.'
    },
    {
      name: 'Grgic Tihomir',
      excerpt:
        'Il signor Staisch è incredibilmente gentile e disponibile. Ha notato subito l’emergenza, ha reagito e dato un buon consiglio. Assolutamente consigliato.'
    },
    {
      name: 'Ilenia Toscano',
      excerpt:
        'Abbiamo avuto una conversazione telefonica. È stato molto disponibile e gentile, ci ha aiutato tantissimo.'
    },
    {
      name: 'Stefan (Local Guide)',
      excerpt:
        'Abbiamo comprato una casa più vecchia e c’era molto da rinnovare, molto più di quanto avevamo previsto. Il signor Staisch e il suo team hanno lavorato in modo professionale, anche con tempi stretti, e ci hanno dato ottimi consigli. Ho potuto fare alcuni lavori da solo (tirare i cavi, posare canaline, collegare le prese) e il signor Staisch ha effettuato il controllo finale. Così il nostro piano ha comunque funzionato.'
    },
    {
      name: 'Lili Wild (LeeWee)',
      excerpt:
        'Abbiamo comprato una casetta nell’area di Stoccarda e c’erano diversi lavori elettrici da fare. Il signor Staisch e il suo team hanno una solida competenza tecnica, danno consigli adatti al cliente. Sono affidabili, cordiali, ascoltano le richieste e rispettano gli appuntamenti. Apprezziamo soprattutto che il signor Staisch pensi in modo trasversale, innovativo e a prova di futuro. Siamo molto soddisfatti: è il nostro elettricista di fiducia, lo abbiamo incaricato più volte e ogni volta siamo rimasti pienamente soddisfatti. Li consigliamo sempre! :-)'
    },
    {
      name: 'Melanie Zipperle',
      excerpt:
        'Team super gentile e competente, il lavoro viene svolto rapidamente e con piena soddisfazione. Anche le richieste urgenti sono state gestite in modo rapido e semplice. Grazie mille, signor Staisch — consigliatissimo!'
    },
    {
      name: 'Simone Zahn',
      excerpt:
        'Molto competenti, puntuali e con ottima consulenza. Sempre di nuovo—è raro trovare un’impresa artigiana così.'
    },
    {
      name: 'Stv.',
      excerpt: 'Il signor Staisch è molto gentile e competente. Lavora in modo rapido e accurato.'
    },
    {
      name: 'Ardan (Local Guide)',
      excerpt:
        'Molto affidabile e uno degli ultimi veri bravi artigiani—un vero professionista! Volentieri ancora.'
    },
    {
      name: 'Ali M (Local Guide)',
      excerpt:
        'Dipendente super cordiale, professionale e molto preciso nel lavoro. Vi ricontatteremo volentieri se necessario. Grazie!'
    },
    {
      name: 'Heike Krämer (Local Guide)',
      excerpt: 'Super gentile, affidabile, competente, puntuale.'
    }
  ]
};

export const Referenzen = () => {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage ?? i18n.language ?? 'de').split('-')[0];
  const testimonials = useMemo(
    () => googleTestimonialsByLang[language] ?? googleTestimonialsByLang.de,
    [language]
  );
  const totalTestimonials = testimonials.length;
  const [requestedPerView, setRequestedPerView] = useState(3);
  const perView = Math.min(requestedPerView, Math.max(totalTestimonials, 1));
  const [activeIndex, setActiveIndex] = useState(() =>
    totalTestimonials ? Math.min(3, totalTestimonials) : 0
  );
  const [isAnimating, setIsAnimating] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const perViewRef = useRef(perView);
  const dragState = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    axis: 'x' | 'y' | null;
  }>({
    active: false,
    startX: 0,
    startY: 0,
    axis: null
  });

  useEffect(() => {
    const updatePerView = () => {
      if (typeof window === 'undefined') {
        return;
      }
      const width = window.innerWidth;
      if (width < 720) {
        setRequestedPerView(1);
      } else if (width < 1024) {
        setRequestedPerView(2);
      } else {
        setRequestedPerView(3);
      }
    };
    updatePerView();
    window.addEventListener('resize', updatePerView);
    return () => window.removeEventListener('resize', updatePerView);
  }, []);

  useEffect(() => {
    if (!totalTestimonials) {
      perViewRef.current = perView;
      return;
    }
    if (perViewRef.current === perView) {
      return;
    }
    setActiveIndex((prev) => {
      const prevPerView = perViewRef.current || 1;
      const normalized =
        (prev - prevPerView + totalTestimonials) % totalTestimonials;
      return normalized + perView;
    });
    perViewRef.current = perView;
  }, [perView, totalTestimonials]);

  const slides = useMemo(() => {
    if (!totalTestimonials) {
      return [];
    }
    const head = testimonials.slice(0, perView);
    const tail = testimonials.slice(-perView);
    return [...tail, ...testimonials, ...head];
  }, [testimonials, totalTestimonials, perView]);

  const snapToIndex = useCallback((index: number) => {
    setIsAnimating(false);
    setActiveIndex(index);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    });
  }, []);

  const normalizedIndex = totalTestimonials
    ? ((activeIndex - perView + totalTestimonials) % totalTestimonials)
    : 0;
  const slidePercent = 100 / perView;

  const goTo = useCallback(
    (index: number) => {
      if (!totalTestimonials) {
        return;
      }
      setIsAnimating(true);
      setActiveIndex(index + perView);
    },
    [perView, totalTestimonials]
  );

  const goPrev = useCallback(() => {
    if (!totalTestimonials) {
      return;
    }
    setIsAnimating(true);
    setActiveIndex((prev) => prev - 1);
  }, [totalTestimonials]);

  const goNext = useCallback(() => {
    if (!totalTestimonials) {
      return;
    }
    setIsAnimating(true);
    setActiveIndex((prev) => prev + 1);
  }, [totalTestimonials]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    },
    [goNext, goPrev]
  );

  const handleTouchStart = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!totalTestimonials) {
        return;
      }
      const touch = event.touches[0];
      dragState.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        axis: null
      };
      setIsDragging(true);
      setDragOffset(0);
      setIsAnimating(false);
    },
    [totalTestimonials]
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!dragState.current.active) {
        return;
      }
      const touch = event.touches[0];
      const deltaX = touch.clientX - dragState.current.startX;
      const deltaY = touch.clientY - dragState.current.startY;

      if (!dragState.current.axis) {
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) {
          return;
        }
        dragState.current.axis =
          Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      }

      if (dragState.current.axis === 'y') {
        dragState.current.active = false;
        setIsDragging(false);
        setIsAnimating(true);
        setDragOffset(0);
        return;
      }

      setDragOffset(deltaX);
    },
    []
  );

  const handleTouchEnd = useCallback(() => {
    if (!dragState.current.active) {
      return;
    }
    dragState.current.active = false;
    setIsDragging(false);
    const viewportWidth = viewportRef.current?.offsetWidth ?? 1;
    const slideWidth = viewportWidth / perView;
    const threshold = Math.min(120, slideWidth * 0.35);

    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset < 0) {
        goNext();
      } else {
        goPrev();
      }
    } else {
      setIsAnimating(true);
    }

    setDragOffset(0);
  }, [dragOffset, goNext, goPrev, perView]);

  const handleTransitionEnd = useCallback(() => {
    if (!totalTestimonials) {
      return;
    }
    if (activeIndex < perView) {
      snapToIndex(activeIndex + totalTestimonials);
    }
    if (activeIndex >= totalTestimonials + perView) {
      snapToIndex(activeIndex - totalTestimonials);
    }
  }, [activeIndex, perView, snapToIndex, totalTestimonials]);

  return (
    <>
      <section className="page-hero" id="top">
        <div className="container page-hero__inner">
          <div>
            <span className="section__eyebrow">{t('referencesPage.hero.eyebrow')}</span>
            <h1>{t('referencesPage.hero.title')}</h1>
            <p className="page-hero__lead">{t('referencesPage.hero.lead')}</p>
          </div>
          <Link className="btn btn--primary" to="/kontakt">
            {t('referencesPage.hero.cta')}
          </Link>
        </div>
      </section>

    <section className="section section--testimonials">
      <div className="container">
        <aside
          className="google-rating"
          aria-label={t('referencesPage.rating.ariaLabel')}
        >
          <div className="google-rating__badge" aria-hidden="true">
            <span className="google-rating__icon">G</span>
            <span className="google-rating__label">{t('referencesPage.rating.badge')}</span>
          </div>
          <div className="google-rating__score">
            <span className="google-rating__value">{t('referencesPage.rating.value')}</span>
            <span className="google-rating__scale">{t('referencesPage.rating.scale')}</span>
          </div>
          <div
            className="google-rating__stars"
            role="img"
            aria-label={t('referencesPage.rating.starsLabel')}
          >
            <span aria-hidden="true">★★★★★</span>
          </div>
          <p className="google-rating__text">{t('referencesPage.rating.text')}</p>
          <a
            className="google-rating__link"
            href="https://www.google.com/search?q=staisch+elektrotechnik"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('referencesPage.rating.link')}
          </a>
        </aside>
        <div
          className="testimonial-carousel"
          role="region"
          aria-roledescription="carousel"
          aria-label={t('referencesPage.carousel.ariaLabel')}
          style={{ '--carousel-columns': perView } as CSSProperties}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <div
            className="testimonial-carousel__viewport"
            aria-live="polite"
            ref={viewportRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            <div
              className={`testimonial-carousel__track${
                isAnimating ? ' is-animating' : ''
              }${isDragging ? ' is-dragging' : ''}`}
              style={{
                transform: `translateX(${
                  -activeIndex * slidePercent +
                  (isDragging && viewportRef.current?.offsetWidth
                    ? (dragOffset / viewportRef.current.offsetWidth) * 100
                    : 0)
                }%)`
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              {slides.map((testimonial, index) => (
                <div
                  className="testimonial-carousel__slide"
                  key={`${testimonial.name}-${index}`}
                  aria-hidden={
                    index < activeIndex || index >= activeIndex + perView
                  }
                >
                  <article className="testimonial">
                    <div className="testimonial__header">
                      <div className="testimonial__meta">
                        <span className="testimonial__name">
                          {testimonial.name}
                        </span>
                        <span className="testimonial__source">
                          {t('referencesPage.testimonial.source')}
                        </span>
                      </div>
                      <div
                        className="testimonial__stars"
                        aria-label={t('referencesPage.testimonial.starsLabel')}
                      >
                        ★★★★★
                      </div>
                    </div>
                    <p className="testimonial__text">{testimonial.excerpt}</p>
                    <span className="testimonial__author">{t('referencesPage.testimonial.excerptLabel')}</span>
                  </article>
                </div>
              ))}
            </div>
          </div>
          <div className="testimonial-carousel__controls">
            <div className="testimonial-carousel__nav">
              <button
                className="testimonial-carousel__button"
                type="button"
                onClick={goPrev}
                aria-label={t('referencesPage.carousel.prev')}
              >
                <ChevronLeftRoundedIcon fontSize="small" />
              </button>
              <button
                className="testimonial-carousel__button"
                type="button"
                onClick={goNext}
                aria-label={t('referencesPage.carousel.next')}
              >
                <ChevronRightRoundedIcon fontSize="small" />
              </button>
            </div>
            <div className="testimonial-carousel__dots" role="tablist">
              {testimonials.map((testimonial, index) => (
                <button
                  key={testimonial.name}
                  type="button"
                  className={`testimonial-carousel__dot${
                    index === normalizedIndex ? ' is-active' : ''
                  }`}
                  onClick={() => goTo(index)}
                  role="tab"
                  aria-label={t('referencesPage.carousel.dotLabel', {
                    name: testimonial.name
                  })}
                  aria-selected={index === normalizedIndex}
                />
              ))}
            </div>
            <div className="testimonial-carousel__counter" aria-live="polite">
              {normalizedIndex + 1} / {totalTestimonials}
            </div>
          </div>
        </div>
      </div>
    </section>

      <CallToAction
        title={t('referencesPage.cta.title')}
        description={t('referencesPage.cta.description')}
        ctaLabel={t('referencesPage.cta.button')}
        ctaTo="https://www.google.com/search?q=staisch+elektrotechnik"
        external
      />
    </>
  );
};
